/**
 * The opt-in card (TMMAPP-0142 item 4, `NEEDS_SCOPE`): the transcript's `SCOPE_REQUEST` line
 * (TMMAPI-0182 U2, J2), so it survives a reload. "To answer, I'd need {categories / entities}:
 * {reason}", with Allow (the session widens, then the turn continues) and Not now (the request is
 * declined, then the turn continues). The model can't widen anything itself; the person decides here
 * (MM-D-45).
 *
 * - **`PENDING`:** Allow / Not now, to the member who asked only. Allow offers only what this person
 *   may add to this session (`grantableScope`): when part of the request can't be added, the card
 *   says what Allow adds; when none of it can, only Not now is offered, so Allow never ends in a 422.
 * - **Any other outcome** is the served one, as a line: "Not allowed", "Allowed", "Allowed in part",
 *   "Nothing changed", "Replaced by a newer question". An outcome this build doesn't know shows no
 *   line. After the person's own choice, focus moves to that line (H5).
 * - **A purged line** (a contributor's data was erased, G2) serves no request: it reads "Removed: a
 *   contributor's data was erased", with its outcome, never an empty card.
 */
import { useEffect, useRef } from 'react';
import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  AssistantCategoryOption,
  AssistantMessage,
  AssistantScopable,
  AssistantScopeEntity,
  AssistantScopeOutcome,
  AssistantScopeRequest,
} from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { AiPill } from '@/components/AiPill';
import { SubmitButton } from '@/components/SubmitButton';
import { formatList } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { categoryLabel, scopeKindLabel } from './assistantCopy';
import { nothingGrantable } from './scopeGrant';
import type { ScopeChoice } from './useConversation';

/** The served outcome's line; `PENDING` has none. */
const OUTCOME_KEYS: Partial<Record<AssistantScopeOutcome, string>> = {
  DECLINED: 'assistant.scopeCard.outcome.declined',
  ACCEPTED: 'assistant.scopeCard.outcome.accepted',
  PARTIAL: 'assistant.scopeCard.outcome.partial',
  NOT_CHANGED: 'assistant.scopeCard.outcome.notChanged',
  SUPERSEDED: 'assistant.scopeCard.outcome.superseded',
};

interface ScopeRequestCardProps {
  /** The `SCOPE_REQUEST` line. */
  line: AssistantMessage;
  /** What Allow can add here (a part of the request, or all of it); null on a purged line. */
  grant: AssistantScopeRequest | null;
  /** The person viewing asked the question: only they choose. */
  mine: boolean;
  choice: ScopeChoice | undefined;
  scopable: readonly AssistantScopable[];
  registry: readonly AssistantCategoryOption[] | null;
  /** No choice can be made (archived, access ended). */
  readOnly: boolean;
  onAllow: () => void;
  onDecline: () => void;
}

export function ScopeRequestCard({
  line,
  grant,
  mine,
  choice,
  scopable,
  registry,
  readOnly,
  onAllow,
  onDecline,
}: ScopeRequestCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const outcomeRef = useRef<HTMLParagraphElement | null>(null);
  const chose = useRef(false);
  const outcome = line.outcome;
  const pending = outcome === 'PENDING';

  // After the person's own Allow or Not now, focus moves to the outcome line (H5).
  useEffect(() => {
    if (!pending && chose.current) {
      chose.current = false;
      outcomeRef.current?.focus();
    }
  }, [pending]);

  const entityName = (entity: AssistantScopeEntity) =>
    scopable.find((item) => item.kind === entity.kind && item.id === entity.id)?.name ??
    scopeKindLabel(t, entity.kind);
  const names = (request: AssistantScopeRequest) => [
    ...request.categories.map((key) => categoryLabel(t, key, registry)),
    ...request.entities.map(entityName),
  ];
  const request = line.scopeRequest;
  const asked = request === null ? [] : names(request);
  const granted = grant === null ? [] : names(grant);
  const none = grant === null || nothingGrantable(grant);
  const partial = !none && granted.length < asked.length;
  const choosing = pending && mine && !readOnly && request !== null;
  const busy = choice?.busy === true;
  const outcomeKey = outcome === null ? undefined : OUTCOME_KEYS[outcome];
  const help = { fontSize: 13, color: tokens.text2 } as const;
  return (
    <Box
      data-scope-card={line.turnId ?? line.id}
      sx={{
        border: `1px solid ${tokens.line}`,
        borderRadius: 1,
        bgcolor: tokens.surface,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box>
        <AiPill />
      </Box>
      {request === null ? (
        <Typography variant="body2" data-scope-removed="">
          {t('assistant.privacy.removed')}
        </Typography>
      ) : (
        <Typography variant="body2">
          {t('assistant.scopeCard.ask', {
            items: formatList(asked, locale),
            reason: request.reason,
          })}
        </Typography>
      )}
      {choosing && none && (
        <Typography sx={help} data-scope-cant-add="">
          {t('assistant.scopeCard.cantAdd')}
        </Typography>
      )}
      {choosing && partial && (
        <Typography sx={help} data-scope-partial="">
          {t('assistant.scopeCard.partial', { items: formatList(granted, locale) })}
        </Typography>
      )}
      {choice !== undefined && choice.error !== null && (
        <Alert severity="error">{errorCopy(t, choice.error, 'assistant.error.generic')}</Alert>
      )}
      {choosing && (
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          {!none && (
            <SubmitButton
              size="small"
              loading={busy}
              onClick={() => {
                chose.current = true;
                onAllow();
              }}
            >
              {t('assistant.scopeCard.allow')}
            </SubmitButton>
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => {
              chose.current = true;
              onDecline();
            }}
          >
            {t('assistant.scopeCard.notNow')}
          </Button>
        </Stack>
      )}
      {outcomeKey !== undefined && (
        <Typography ref={outcomeRef} tabIndex={-1} sx={help} data-scope-outcome={outcome}>
          {t(outcomeKey)}
        </Typography>
      )}
    </Box>
  );
}
